import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { connect } from 'react-redux'
import { useTheme, Stack, Tooltip } from '@mui/material'
import { CheckCircle, Warning, Error as ErrorIcon } from '@mui/icons-material'
import { DataGrid } from '@mui/x-data-grid'
import { useTranslation } from 'react-i18next'
import { DateTime } from 'luxon'

import Layout from '../../common/components/Layout/Layout'
import VisitForm from '../../common/components/VisitForm/VisitForm'
import './Visits.sass'

import {
  setLoading,
  defaultSuccess,
  defaultCatch,
  _dispatch as dispatch
} from '../../state/actions'

import { debounce, getCompany, getNoRowOverlay } from '../../common/utilities/utilities'
import VisitorsFilter from '../../common/components/VisitorsFilter/VisitorsFilter'
import TableMenuGenerator from '../../common/components/TableMenuGenerator/TableMenuGenerator'
import TableButtonGenerator from '../../common/components/TableButtonGenerator/TableButtonGenerator'
import { parseVisitDateTime, removeDatefromDateTime } from '../../common/utilities/FormatDate'
import EditDialog from '../../common/components/EditDialog/EditDialog'
import DetailDialog from '../../common/components/DetailDialog/DetailDialog'
import ResizableHeader from './ResizableHeader'
import { Types } from '../../state/actionTypes'
import db from '../../db/db'
import { runPoll } from '../../sync/SyncEngine'
import { api } from '../../api/api'

function SyncStatusIcon({ status, reason }) {
  const { t } = useTranslation()
  const theme = useTheme()
  const iconSx = { fontSize: 24, display: 'block' }
  if (status === 'pending') {
    return (
      <Tooltip title={t('sync_pending')}>
        <Warning sx={{ ...iconSx, color: '#CB7B01' }} />
      </Tooltip>
    )
  }
  if (status === 'failed') {
    return (
      <Tooltip title={reason ? `${t('sync_failed')}: ${reason}` : t('sync_failed')}>
        <ErrorIcon sx={{ ...iconSx, color: theme.palette.error.main }} />
      </Tooltip>
    )
  }
  return (
    <Tooltip title={t('sync_done')}>
      <CheckCircle sx={{ ...iconSx, color: '#128724' }} />
    </Tooltip>
  )
}

const COLUMN_WIDTHS_KEY = 'lobby.visits.columnWidths'

const MIN_COLUMN_WIDTHS = {
  identification: 80,
  name: 120,
  license_plate: 100,
  destination: 100,
  comes_from: 80,
  entry_time: 120,
  exit_time: 120,
  card_number: 80,
  visit_type: 100,
  syncStatus: 56,
  actions: 56,
}

function readStoredColumnWidths() {
  try {
    const raw = localStorage.getItem(COLUMN_WIDTHS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return {}
    const widths = {}
    Object.entries(parsed).forEach(([field, value]) => {
      const minWidth = MIN_COLUMN_WIDTHS[field]
      const width = Number(value)
      if (minWidth == null || !Number.isFinite(width) || width < minWidth) return
      widths[field] = width
    })
    return widths
  } catch (err) {
    return {}
  }
}

function isTodayOrFuture(entryTime) {
  if (!entryTime) return true
  const parsed = parseVisitDateTime(entryTime)
  if (!parsed) return true
  return parsed.setZone('local').startOf('day') >= DateTime.local().startOf('day')
}

function Visitors(props) {
  const {
    dispatch,
    ask_visit_comes_from = false,
    ask_visit_identification = false,
    ask_visit_license_plate = false,
    custom_visit_identification_label = '',
    companies = [],
    visitDestinations = [],
    building_id,
    enabled_visit_types,
    isOnline,
    defaultSuccess,
  } = props

  const refBuilding = useRef()
  const { t } = useTranslation()
  const theme = useTheme()
  const identificationLabel = custom_visit_identification_label || t('Identification')

  const [allVisits, setAllVisits] = useState([])
  const [pageSize, setPageSize] = useState(15)
  const [prefix, setPrefix] = useState('')
  const [selectedItem, setSelectedItem] = useState({})
  const [detail, setDetail] = useState(false)
  const [edit, setEdit] = useState(false)
  const [destinationIds, setDestinationIds] = useState([])
  const [visitStatus, setVisitStatus] = useState('-1')
  const [columnWidths, setColumnWidths] = useState(readStoredColumnWidths)
  const [resizingField, setResizingField] = useState(null)
  const columnWidthsRef = useRef(columnWidths)
  const endDragRef = useRef(null)
  const mountedRef = useRef(true)

  useEffect(() => () => {
    mountedRef.current = false
    endDragRef.current?.()
  }, [])

  const persistColumnWidths = useCallback(() => {
    try {
      localStorage.setItem(COLUMN_WIDTHS_KEY, JSON.stringify(columnWidthsRef.current))
    } catch (err) {
      // localStorage puede no estar disponible
    }
  }, [])

  const onColumnResize = useCallback((field, width) => {
    const next = { ...columnWidthsRef.current, [field]: width }
    columnWidthsRef.current = next
    setColumnWidths(next)
  }, [])

  const onResizeStart = useCallback((field, clientX, startWidth) => {
    endDragRef.current?.()
    const minWidth = MIN_COLUMN_WIDTHS[field] ?? 56
    const baseWidth = startWidth ?? columnWidthsRef.current[field] ?? minWidth
    if (mountedRef.current) setResizingField(field)

    const onMove = event => {
      if (event.cancelable) event.preventDefault()
      const next = Math.max(minWidth, Math.round(baseWidth + (event.clientX - clientX)))
      onColumnResize(field, next)
    }

    const endDrag = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', endDrag)
      window.removeEventListener('pointercancel', endDrag)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      if (endDragRef.current === endDrag) endDragRef.current = null
      if (mountedRef.current) setResizingField(null)
      persistColumnWidths()
    }

    endDragRef.current = endDrag
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    window.addEventListener('pointermove', onMove, { passive: false })
    window.addEventListener('pointerup', endDrag)
    window.addEventListener('pointercancel', endDrag)
  }, [onColumnResize, persistColumnWidths])

  const loadVisitsFromDB = useCallback(async () => {
    if (!building_id) return
    try {
      const raw = await db.visits
        .where('building_id').equals(building_id)
        .toArray()

      const filtered = raw
        .filter(v => isTodayOrFuture(v.entry_time))
        .sort((a, b) => {
          const ta = parseVisitDateTime(a.entry_time)?.toMillis() ?? 0
          const tb = parseVisitDateTime(b.entry_time)?.toMillis() ?? 0
          return tb - ta
        })

      setAllVisits(filtered)
    } catch (err) {
      console.error('[Visits] Error cargando desde IndexedDB:', err)
    }
  }, [building_id])

  useEffect(() => {
    if (!refBuilding.current) {
      refBuilding.current = 1
    }
    loadVisitsFromDB()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [building_id])

  useEffect(() => {
    const handler = () => loadVisitsFromDB()
    window.addEventListener('lobby:sync-complete', handler)
    window.addEventListener('lobby:sync-error', handler)
    window.addEventListener('lobby:visit-pending', handler)
    window.addEventListener('lobby:push-applied', handler)
    return () => {
      window.removeEventListener('lobby:sync-complete', handler)
      window.removeEventListener('lobby:sync-error', handler)
      window.removeEventListener('lobby:visit-pending', handler)
      window.removeEventListener('lobby:push-applied', handler)
    }
  }, [loadVisitsFromDB])

  const handleDevices = useCallback(
    mediaDevices =>
      dispatch({
        type: Types.SET_CAMERAS,
        payload: mediaDevices.filter(({ kind }) => kind === 'videoinput'),
      }),
    [dispatch]
  )

  useEffect(() => {
    const mediaDevices = navigator.mediaDevices
    const canEnumerateDevices =
      window.isSecureContext &&
      mediaDevices &&
      typeof mediaDevices.enumerateDevices === 'function'

    if (!canEnumerateDevices) {
      dispatch({ type: Types.SET_CAMERAS, payload: [] })
      return undefined
    }

    const getDevices = () => {
      mediaDevices.enumerateDevices()
        .then(handleDevices)
        .catch(() => dispatch({ type: Types.SET_CAMERAS, payload: [] }))
    }

    getDevices()
    mediaDevices.addEventListener?.('devicechange', getDevices)
    return () => mediaDevices.removeEventListener?.('devicechange', getDevices)
  }, [dispatch, handleDevices])

  const onShowDetail = useCallback(_item => {
    setSelectedItem(_item)
    setDetail(true)
  }, [])

  const onShowEdit = useCallback(_item => {
    setSelectedItem(_item)
    setEdit(true)
  }, [])

  const exitConfirmation = useCallback(async (row) => {
    const uuid = row.uuid
    if (!uuid) return

    if (isOnline && row.id) {
      try {
        const response = await api.put(`/visit/${row.id}`)
        defaultSuccess(response)
        const { data } = response
        if (data?.success !== false && data?.visit) {
          await db.visits.put({
            ...row,
            ...data.visit,
            uuid,
            syncStatus: 'synced',
            syncError: null,
            pushAttempts: 0,
          })
          window.dispatchEvent(new CustomEvent('lobby:visit-pending'))
          loadVisitsFromDB()
          return
        }
      } catch (error) {
        console.warn('[Visits] Error marcando salida online, se encola para reintento:', error)
      }
    }

    const exitTime = new Date().toISOString()
    await db.visits.update(uuid, {
      exit_time: exitTime,
      syncStatus: 'pending',
      pushAttempts: 0,
      _pendingChanges: { exit_time: exitTime },
    })
    window.dispatchEvent(new CustomEvent('lobby:visit-pending'))
    loadVisitsFromDB()
    if (isOnline) {
      // `runPoll`: sube la salida sin un `runSync` completo.
      runPoll()
        .catch(console.error)
    }
  }, [isOnline, loadVisitsFromDB, defaultSuccess])

  const filteredVisits = useMemo(() => {
    let result = allVisits

    if (prefix) {
      const lower = prefix.toLowerCase()
      result = result.filter(v => {
        const name = `${v.first_name ?? ''} ${v.last_name_one ?? ''} ${v.last_name_two ?? ''}`.toLowerCase()
        const id = (v.identification ?? '').toLowerCase()
        const card = (v.card_number ?? '').toLowerCase()
        return name.includes(lower) || id.includes(lower) || card.includes(lower)
      })
    }

    if (destinationIds.length) {
      const ids = destinationIds.map(d => d.id)
      result = result.filter(v => {
        const destId = v.destination?.id ?? v.destination_id
        return ids.includes(destId)
      })
    }

    if (visitStatus === '1') result = result.filter(v => v.exit_time)
    if (visitStatus === '2') result = result.filter(v => !v.exit_time)

    return result
  }, [allVisits, prefix, destinationIds, visitStatus])

  const columns = useMemo(() => {
    const withResize = col => {
      const minWidth = MIN_COLUMN_WIDTHS[col.field] ?? 56
      const width = columnWidths[col.field] ?? col.width
      return {
        ...col,
        width,
        minWidth,
        renderHeader: () => (
          <ResizableHeader
            label={col.headerName}
            align={col.headerAlign}
            resizing={resizingField === col.field}
            onResizeStart={clientX => onResizeStart(col.field, clientX, width)}
          />
        ),
      }
    }

    return [
      ...(ask_visit_identification ? [{
        sortable: false,
        headerName: identificationLabel,
        field: 'identification',
        width: 200,
      }] : []),
      {
        sortable: false,
        headerName: t('name'),
        field: 'name',
        width: 280,
        valueGetter: ({ row }) => {
          const first_name = row.first_name ?? ''
          const last_name_one = row.last_name_one ?? ''
          const last_name_two = row.last_name_two ?? ''
          return `${first_name} ${last_name_one} ${last_name_two}`.trim()
        },
      },
      ...(ask_visit_license_plate ? [{
        sortable: false,
        headerName: t('license_plate'),
        field: 'license_plate',
        width: 300,
      }] : []),
      {
        sortable: false,
        headerName: t('Go to'),
        field: 'destination',
        width: 180,
        renderCell: ({ row }) => {
          if (row.destination?.name) return row.destination.name
          if (row.destination_id) {
            const dest = visitDestinations.find(d => d.id === row.destination_id)
            return dest?.name || getCompany(row.company_id, companies)
          }
          return getCompany(row.company_id, companies)
        },
      },
      ...(ask_visit_comes_from ? [{
        sortable: false,
        headerName: t('Comes from'),
        field: 'comes_from',
        width: 150,
      }] : []),
      {
        sortable: false,
        headerName: t('Entry time'),
        field: 'entry_time',
        width: 180,
        valueFormatter: ({ value }) => removeDatefromDateTime(value),
      },
      {
        sortable: false,
        headerName: t('Exit time'),
        field: 'exit_time',
        width: 150,
        headerClassName: 'transparent',
        renderCell: props => (
          <TableButtonGenerator
            exitConfirmation={exitConfirmation}
            {...props}
          />
        ),
      },
      {
        sortable: false,
        headerName: t('Card'),
        field: 'card_number',
        width: 150,
      },
      ...(enabled_visit_types ? [{
        sortable: false,
        headerName: t('Visit type'),
        field: 'visit_type',
        valueGetter: ({ value = {} }) => value?.name || '',
        width: 300,
      }] : []),
      {
        sortable: false,
        headerName: t('sync_status'),
        field: 'syncStatus',
        width: 72,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ value, row }) => <SyncStatusIcon status={value} reason={row.syncError} />,
      },
      {
        sortable: false,
        headerName: '',
        field: 'actions',
        headerClassName: 'transparent',
        width: 100,
        renderCell: props => (
          <TableMenuGenerator
            onShowDetail={onShowDetail}
            onShowEdit={onShowEdit}
            {...props}
          />
        ),
      },
    ].map(withResize)
  }, [
    ask_visit_identification, ask_visit_license_plate, ask_visit_comes_from,
    companies, visitDestinations, enabled_visit_types, exitConfirmation,
    identificationLabel, onShowDetail, onShowEdit, t,
    columnWidths, resizingField, onResizeStart,
  ])

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onEfficientSearch = useCallback(
    debounce(value => setPrefix(value), 500),
    []
  )

  const onChangePrefix = value => {
    onEfficientSearch(value)
  }

  const labelDisplayedRows = useCallback(
    ({ from, to, count }) =>
      `${from}–${to} ${t('of')} ${count !== -1 ? count : `${t('more than')} ${to}`}`,
    [t]
  )

  const onCloseEditDialog = () => {
    setEdit(false)
    setSelectedItem({})
  }

  const handleChangeDestination = useCallback((_, ids) => {
    setDestinationIds(ids)
  }, [])

  const handleChangeVisitStatus = useCallback(evt => {
    setVisitStatus(evt.target.value)
  }, [])

  const handleFilterData = useCallback(() => {
  }, [])

  return (
    <Layout className="Visitors">
      <VisitForm
        exitConfirmation={exitConfirmation}
        editConfirmation={onShowEdit}
        loadVisits={loadVisitsFromDB}
        visits={allVisits}
      />
      <div className='visit-list'>
        <Stack className='main-wrapper' direction="column" spacing={2}>
          <VisitorsFilter
            prefix={prefix}
            setPrefix={onChangePrefix}
            destinationIds={destinationIds}
            visitStatus={visitStatus}
            handleChangeDestination={handleChangeDestination}
            handleChangeVisitStatus={handleChangeVisitStatus}
            handleFilterData={handleFilterData}
          />
          <div className='table-wrapper'>
            <DataGrid
              rows={filteredVisits}
              columns={columns}
              getRowId={row => row.uuid}
              disableSelectionOnClick
              disableColumnMenu
              components={{
                NoRowsOverlay: () => getNoRowOverlay(theme, t),
                NoResultsOverlay: () => getNoRowOverlay(theme, t),
              }}
              componentsProps={{
                pagination: {
                  labelRowsPerPage: t('rows per page:'),
                  labelDisplayedRows: labelDisplayedRows,
                },
              }}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              rowsPerPageOptions={[15, 50, 100]}
              pagination
            />
          </div>
        </Stack>
      </div>
      <DetailDialog
        open={detail}
        onClose={() => setDetail(false)}
        item={selectedItem}
      />
      <EditDialog
        open={edit}
        onClose={onCloseEditDialog}
        fetchVisits={loadVisitsFromDB}
        item={selectedItem}
      />
    </Layout>
  )
}

const mapStateToProps = state => ({
  ask_visit_comes_from: state.profile.ask_visit_comes_from,
  ask_visit_identification: state.profile.ask_visit_identification,
  companies: state.profile.companies,
  visitDestinations: state.profile.visitDestinations,
  building_id: state.profile.building_id,
  enabled_visit_types: state.profile.enabled_visit_types,
  ask_visit_license_plate: state.profile.ask_visit_license_plate,
  custom_visit_identification_label: state.profile.custom_visit_identification_label,
  isOnline: state.utilities.isOnline,
})

const mapDispatchToProps = {
  dispatch,
  setLoading,
  defaultSuccess,
  defaultCatch,
}

export default connect(mapStateToProps, mapDispatchToProps)(Visitors)
